import { atMinuteOfDay, dayKeyOf, toIso, weekdayOfDay, addDaysToKey } from '../../domain/time';
import { BUSINESS_TZ } from '../../domain/types';
import type { Clock, KeyValueStore, Unsubscribe } from '../types';

const OFFSET_KEY = 'clock.offset';
const ANCHORED_KEY = 'clock.anchoredAt';

/** The demo starts at 10:00 Asia/Jerusalem on the current working day, whatever the real time of day is. Fridays and Saturdays start on Sunday. */
export function demoAnchorNow(realNow: number, tz: string = BUSINESS_TZ): number {
  let day = dayKeyOf(realNow, tz);
  const wd = weekdayOfDay(day); // 0 = Sunday
  if (wd === 5) day = addDaysToKey(day, 2);
  else if (wd === 6) day = addDaysToKey(day, 1);
  return atMinuteOfDay(day, 10 * 60, tz);
}

/** Real time plus a persisted offset. `advance` is how the demo fast-forwards to reminder time. */
export function createDemoClock(opts: { kv: KeyValueStore; realNow?: () => number; /** Start (and reset) at the fixed demo time of day instead of real time. */ anchor?: boolean }): Clock {
  const realNow = opts.realNow ?? Date.now;
  const listeners = new Set<(n: number) => void>();
  const raw = opts.kv.get(OFFSET_KEY);
  const stored = Number(raw);
  const anchorOffset = (): number => demoAnchorNow(realNow()) - realNow();
  let offset = raw !== null && Number.isFinite(stored) ? stored : opts.anchor ? anchorOffset() : 0;
  if (opts.anchor && raw === null) { opts.kv.set(OFFSET_KEY, String(offset)); opts.kv.set(ANCHORED_KEY, String(realNow())); }
  const now = (): number => realNow() + offset;
  const change = (next: number): void => {
    offset = next;
    opts.kv.set(OFFSET_KEY, String(offset));
    for (const cb of [...listeners]) cb(now());
  };
  return {
    now,
    nowIso: () => toIso(now()),
    advance: (ms) => change(offset + ms),
    setNow: (ms) => change(ms - realNow()),
    reset: () => { if (opts.anchor) opts.kv.set(ANCHORED_KEY, String(realNow())); change(opts.anchor ? anchorOffset() : 0); },
    anchoredAt: () => { const v = Number(opts.kv.get(ANCHORED_KEY)); return Number.isFinite(v) && v > 0 ? v : undefined; },
    offsetMs: () => offset,
    subscribe(cb): Unsubscribe { listeners.add(cb); return () => void listeners.delete(cb); },
  };
}
